import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, fmtGYD, apiErr, mediaUrl } from "@/lib/api";
import { idb } from "@/lib/idb";
import { enqueue, runSync, startSyncEngine, subscribe, syncNow } from "@/lib/offline";
import { DEVICE_ID, printReceipt } from "@/lib/device";
import { StatusBadge } from "@/components/StatusBadge";
import { ZReportModal } from "@/components/ZReport";
import { ReturnModal } from "@/components/ReturnModal";
import { OnlineOrdersModal } from "@/components/OnlineOrders";
import { Code128 } from "@/components/Barcode";
import {
  Search, Barcode, Plus, Minus, Trash2, Pause, Play, CreditCard, X, Printer,
  User, LogOut, Clock, ShoppingCart, RotateCcw, Lock, Banknote, Undo2, Globe, RefreshCw,
} from "lucide-react";

const uuid = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);

function computeTotals(items, orderDiscount, tax) {
  const rate = tax?.tax_rate || 0;
  const inclusive = tax?.tax_inclusive;
  let subtotal = 0, taxTotal = 0;
  items.forEach((it) => {
    const line = it.quantity * it.unit_price - (it.discount || 0);
    subtotal += line;
    if (!it.tax_exempt) {
      if (inclusive) taxTotal += rate ? line - line / (1 + rate / 100) : 0;
      else taxTotal += (line * rate) / 100;
    }
  });
  subtotal -= orderDiscount || 0;
  const total = inclusive ? subtotal : subtotal + taxTotal;
  return { subtotal: +subtotal.toFixed(2), taxTotal: +taxTotal.toFixed(2), total: +total.toFixed(2) };
}

const RECEIPT_RE = /^[A-Z0-9]+-\d{8}-[A-Z0-9]{6}$/i;

export default function POS() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [activeCat, setActiveCat] = useState("all");
  const [search, setSearch] = useState("");
  const [cart, setCart] = useState([]);
  const [tax, setTax] = useState({ tax_rate: 0, tax_inclusive: false, currency: "GYD" });
  const [store, setStore] = useState(null);
  const [register, setRegister] = useState(null);
  const [merchant, setMerchant] = useState(null);
  const [shift, setShift] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [held, setHeld] = useState([]);
  const [heldOpen, setHeldOpen] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [closeOpen, setCloseOpen] = useState(false);
  const [returnOpen, setReturnOpen] = useState(false);
  const [returnCode, setReturnCode] = useState("");
  const [orders, setOrders] = useState([]);
  const [ordersOpen, setOrdersOpen] = useState(false);
  const [syncBusy, setSyncBusy] = useState(false);
  const [toast, setToast] = useState("");
  const barcodeRef = useRef(null);

  const totals = useMemo(() => computeTotals(cart, 0, tax), [cart, tax]);
  const openOrders = orders.filter((o) => !["fulfilled", "cancelled"].includes(o.status)).length;

  const flash = (m) => { setToast(m); setTimeout(() => setToast(""), 2500); };

  useEffect(() => {
    startSyncEngine(DEVICE_ID);
    (async () => {
      // Load from cache first (offline-first)
      const cached = await idb.all("products");
      if (cached?.length) setProducts(cached);
      const cachedCats = (await idb.get("meta", "cats"))?.value;
      if (cachedCats) setCats(cachedCats);
      const cachedMeta = (await idb.get("meta", "context"))?.value;
      if (cachedMeta) { setStore(cachedMeta.store); setRegister(cachedMeta.register); setMerchant(cachedMeta.merchant); setTax(cachedMeta.tax); }
      const cachedHeld = await idb.all("held_sales");
      setHeld(cachedHeld || []);
      try {
        const [p, c, s, r, m, t, cust, sh] = await Promise.all([
          api.get("/products"), api.get("/categories"), api.get("/stores"),
          api.get("/registers"), api.get("/merchants"), api.get("/tax-settings"),
          api.get("/customers"), api.get("/shifts", { params: { status: "open" } }),
        ]);
        setProducts(p.data); await idb.putAll("products", p.data);
        setCats(c.data); await idb.put("meta", { id: "cats", value: c.data });
        const st = s.data[0], rg = r.data[0], mc = m.data[0], tx = t.data || {};
        setStore(st); setRegister(rg); setMerchant(mc); setTax(tx);
        setCustomers(cust.data);
        await idb.put("meta", { id: "context", value: { store: st, register: rg, merchant: mc, tax: tx } });
        const openShift = (sh.data || []).find((x) => x.cashier_id === user.id) || sh.data?.[0];
        if (openShift) setShift(openShift);
      } catch (e) { flash("Offline — using cached data"); }
    })();
    // Two-way sync engine (lib/offline.js) pulls catalog/photos/stock/online orders; reload from local DB when it changes
    let lastVersion = 0;
    const unsub = subscribe(async (s) => {
      if (s.pullVersion === lastVersion) return;
      lastVersion = s.pullVersion;
      const [prods, cust, ords, catsMeta] = await Promise.all([idb.all("products"), idb.all("customers"), idb.all("online_orders"), idb.get("meta", "cats")]);
      if (prods?.length) setProducts(prods);
      if (cust?.length) setCustomers(cust);
      setOrders((ords || []).sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")));
      if (catsMeta?.value) setCats(catsMeta.value);
    });
    return unsub;
  }, [user.id]);

  const addToCart = (p) => {
    setCart((c) => {
      const ex = c.find((x) => x.product_id === p.id);
      if (ex) return c.map((x) => x.product_id === p.id ? { ...x, quantity: x.quantity + 1 } : x);
      return [...c, {
        product_id: p.id, name: p.name, quantity: 1,
        unit_price: p.promo_price || p.selling_price, discount: 0,
        tax_rate: tax.tax_rate, tax_exempt: p.tax_exempt, stock: p.quantity,
      }];
    });
  };

  const onBarcode = (e) => {
    if (e.key !== "Enter") return;
    const code = e.target.value.trim();
    if (!code) return;
    const p = products.find((x) => x.barcode === code || (x.additional_barcodes || []).includes(code) || x.sku === code);
    if (p) { addToCart(p); e.target.value = ""; }
    else if (RECEIPT_RE.test(code)) { setReturnCode(code.toUpperCase()); setReturnOpen(true); e.target.value = ""; }
    else flash(`No product for barcode ${code}`);
  };

  const setQty = (id, d) => setCart((c) => c.map((x) => x.product_id === id ? { ...x, quantity: Math.max(1, x.quantity + d) } : x));
  const removeItem = (id) => setCart((c) => c.filter((x) => x.product_id !== id));

  const holdSale = async () => {
    if (!cart.length) return;
    const h = { id: uuid(), cart, customer, at: new Date().toISOString(), total: totals.total };
    await idb.put("held_sales", h);
    setHeld((x) => [...x, h]); setCart([]); setCustomer(null); flash("Sale held");
  };
  const retrieveHeld = async (h) => { setCart(h.cart); setCustomer(h.customer); await idb.del("held_sales", h.id); setHeld((x) => x.filter((y) => y.id !== h.id)); setHeldOpen(false); };

  const filtered = products.filter((p) =>
    (activeCat === "all" || p.category_id === activeCat) &&
    (!search || p.name.toLowerCase().includes(search.toLowerCase()) || (p.sku || "").toLowerCase().includes(search.toLowerCase()))
  );

  if (!shift) {
    return <ShiftGate user={user} register={register} store={store} onOpen={setShift} logout={() => { logout(); nav("/login"); }} />;
  }

  return (
    <div className="pos-dark min-h-screen flex flex-col">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-800 bg-slate-900/60">
        <div className="flex items-center gap-4 text-sm">
          <div className="font-bold text-indigo-400" data-testid="pos-merchant-name">{merchant?.name || "MatrixShopping"}</div>
          <span className="text-slate-600">|</span>
          <span className="text-slate-300">{store?.name}</span>
          <span className="text-slate-500 mono" data-testid="pos-register">{register?.register_number || "R1"}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-emerald-400 flex items-center gap-1"><Clock size={13} /> Shift Open</span>
          <button data-testid="pos-online-orders" onClick={() => setOrdersOpen(true)} className="touch-btn text-xs px-2.5 py-1.5 rounded-lg ms-card ms-card-hover flex items-center gap-1 text-indigo-300 relative"><Globe size={13} /> Orders{openOrders > 0 && <span data-testid="pos-online-orders-count" className="ml-1 bg-indigo-600 text-white rounded-full px-1.5 text-[10px] font-bold">{openOrders}</span>}</button>
          <button data-testid="pos-sync-now" disabled={syncBusy} onClick={async () => { setSyncBusy(true); const r = await syncNow(DEVICE_ID); setSyncBusy(false); flash(r.lastError ? `Sync: ${r.lastError}` : `Synced — ${r.push?.accepted || 0} uploaded, ${r.pull?.downloaded || 0} downloaded`); }} className="touch-btn text-xs px-2.5 py-1.5 rounded-lg ms-card ms-card-hover flex items-center gap-1 text-slate-200 disabled:opacity-50"><RefreshCw size={13} className={syncBusy ? "animate-spin" : ""} /> Sync now</button>
          <button data-testid="pos-returns" onClick={() => setReturnOpen(true)} className="touch-btn text-xs px-2.5 py-1.5 rounded-lg ms-card ms-card-hover flex items-center gap-1 text-sky-300"><Undo2 size={13} /> Returns</button>
          <button data-testid="pos-close-shift" onClick={() => setCloseOpen(true)} className="touch-btn text-xs px-2.5 py-1.5 rounded-lg ms-card ms-card-hover flex items-center gap-1 text-amber-300"><Lock size={13} /> Close Shift</button>
          <StatusBadge />
          <div className="flex items-center gap-1.5 text-sm text-slate-300"><User size={15} />{user.name}</div>
          <button data-testid="pos-logout" onClick={async () => { await logout(); nav("/login"); }} className="touch-btn text-slate-400 hover:text-rose-400"><LogOut size={18} /></button>
        </div>
      </div>

      <div className="flex-1 grid grid-cols-[1fr_400px] overflow-hidden">
        {/* Left: products */}
        <div className="flex flex-col overflow-hidden p-4">
          <div className="flex gap-2 mb-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-2.5 text-slate-500" size={18} />
              <input data-testid="pos-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search product by name or SKU"
                className="w-full bg-slate-900 border border-slate-700 rounded-lg pl-10 pr-3 py-2.5 text-sm outline-none focus:border-indigo-500" />
            </div>
            <div className="relative w-56">
              <Barcode className="absolute left-3 top-2.5 text-slate-500" size={18} />
              <input data-testid="pos-barcode-input" ref={barcodeRef} onKeyDown={onBarcode} placeholder="Scan barcode + Enter"
                className="w-full bg-slate-900 border border-slate-700 rounded-lg pl-10 pr-3 py-2.5 text-sm outline-none focus:border-indigo-500 mono" />
            </div>
          </div>
          <div className="flex gap-2 mb-3 overflow-x-auto pb-1">
            <button onClick={() => setActiveCat("all")} className={`touch-btn px-3.5 py-1.5 rounded-lg text-sm whitespace-nowrap ${activeCat === "all" ? "bg-indigo-600" : "ms-card ms-card-hover"}`}>All</button>
            {cats.map((c) => (
              <button key={c.id} data-testid={`pos-cat-${c.id}`} onClick={() => setActiveCat(c.id)} className={`touch-btn px-3.5 py-1.5 rounded-lg text-sm whitespace-nowrap ${activeCat === c.id ? "bg-indigo-600" : "ms-card ms-card-hover"}`}>{c.name}</button>
            ))}
          </div>
          <div className="grid grid-cols-3 lg:grid-cols-4 gap-3 overflow-y-auto content-start">
            {filtered.map((p) => {
              const price = p.promo_price || p.selling_price;
              const low = p.quantity <= (p.reorder_level || 0);
              return (
                <button key={p.id} data-testid={`pos-product-${p.id}`} onClick={() => addToCart(p)}
                  className="product-tile ms-card ms-card-hover touch-btn text-left p-3 rounded-xl flex flex-col justify-between">
                  <div className="flex items-start gap-2">
                    {p.images?.[0] && <img src={mediaUrl(p.images[0].thumb)} alt="" data-testid={`pos-product-img-${p.id}`} loading="lazy" onError={(e) => { e.currentTarget.style.display = "none"; }} className="w-10 h-10 rounded-md object-cover border border-slate-700 flex-shrink-0" />}
                    <div className="font-semibold text-sm leading-tight">{p.name}</div>
                  </div>
                  <div className="mt-2 flex items-end justify-between">
                    <div className="mono font-bold text-indigo-300">{fmtGYD(price)}</div>
                    <div className={`text-[11px] px-1.5 py-0.5 rounded ${low ? "bg-amber-500/20 text-amber-300" : "bg-slate-700/50 text-slate-300"}`}>
                      {low ? "Low: " : "Stock: "}<span className="mono">{p.quantity}</span>
                    </div>
                  </div>
                </button>
              );
            })}
            {filtered.length === 0 && <div className="col-span-full text-center text-slate-500 py-12">No products found</div>}
          </div>
        </div>

        {/* Right: cart */}
        <div className="border-l border-slate-800 bg-slate-900/40 flex flex-col">
          <div className="p-4 border-b border-slate-800 flex items-center justify-between">
            <div className="flex items-center gap-2 font-semibold"><ShoppingCart size={18} /> Cart ({cart.length})</div>
            <div className="flex gap-2">
              <button data-testid="pos-hold" onClick={holdSale} className="touch-btn text-xs px-2.5 py-1.5 rounded-lg ms-card ms-card-hover flex items-center gap-1"><Pause size={14} /> Hold</button>
              <button data-testid="pos-retrieve" onClick={() => setHeldOpen(true)} className="touch-btn text-xs px-2.5 py-1.5 rounded-lg ms-card ms-card-hover flex items-center gap-1"><Play size={14} /> Retrieve {held.length ? `(${held.length})` : ""}</button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {cart.length === 0 && <div className="text-center text-slate-600 py-16 text-sm">Tap a product or scan a barcode</div>}
            {cart.map((it) => (
              <div key={it.product_id} data-testid={`cart-item-${it.product_id}`} className="ms-card rounded-lg p-2.5 flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{it.name}</div>
                  <div className="text-xs text-slate-400 mono">{fmtGYD(it.unit_price)} × {it.quantity}</div>
                </div>
                <div className="flex items-center gap-1">
                  <button data-testid={`cart-minus-${it.product_id}`} onClick={() => setQty(it.product_id, -1)} className="touch-btn w-8 h-8 rounded-md bg-slate-800 flex items-center justify-center"><Minus size={14} /></button>
                  <span className="w-7 text-center mono">{it.quantity}</span>
                  <button data-testid={`cart-plus-${it.product_id}`} onClick={() => setQty(it.product_id, 1)} className="touch-btn w-8 h-8 rounded-md bg-slate-800 flex items-center justify-center"><Plus size={14} /></button>
                </div>
                <div className="w-20 text-right mono font-semibold text-sm">{fmtGYD(it.unit_price * it.quantity)}</div>
                <button data-testid={`cart-remove-${it.product_id}`} onClick={() => removeItem(it.product_id)} className="touch-btn text-slate-500 hover:text-rose-400"><Trash2 size={16} /></button>
              </div>
            ))}
          </div>

          <div className="p-4 border-t border-slate-800 space-y-2">
            <CustomerPicker customers={customers} customer={customer} setCustomer={setCustomer} onCreate={async (c) => {
              try { const { data } = await api.post("/customers", c); setCustomers((x) => [...x, data]); setCustomer(data); } catch (e) { flash(apiErr(e)); }
            }} />
            <div className="flex justify-between text-sm text-slate-400"><span>Subtotal</span><span className="mono">{fmtGYD(totals.subtotal)}</span></div>
            <div className="flex justify-between text-sm text-slate-400"><span>{tax.tax_name || "Tax"} ({tax.tax_rate || 0}%)</span><span className="mono">{fmtGYD(totals.taxTotal)}</span></div>
            <div className="flex justify-between text-xl font-bold"><span>Total</span><span className="mono text-indigo-300" data-testid="pos-cart-total">{fmtGYD(totals.total)}</span></div>
            <div className="grid grid-cols-4 gap-2 pt-1">
              <button data-testid="pos-cancel" onClick={() => { setCart([]); setCustomer(null); }} className="touch-btn py-3 rounded-lg bg-slate-800 hover:bg-slate-700 text-sm flex items-center justify-center"><RotateCcw size={16} /></button>
              <button data-testid="pos-pay" disabled={!cart.length} onClick={() => setPayOpen(true)}
                className="col-span-3 touch-btn py-3 rounded-lg bg-emerald-600 hover:bg-emerald-500 font-bold flex items-center justify-center gap-2 disabled:opacity-50" style={{ minHeight: 60 }}>
                <CreditCard size={20} /> Pay {fmtGYD(totals.total)}
              </button>
            </div>
          </div>
        </div>
      </div>

      {payOpen && (
        <PaymentModal total={totals.total} onClose={() => setPayOpen(false)} onConfirm={async (payments, change) => {
          const idempotency_key = uuid();
          const receipt_no = `${register?.register_number || "R1"}-${new Date().toISOString().slice(0, 10).replace(/-/g, "")}-${idempotency_key.replace(/-/g, "").slice(0, 6).toUpperCase()}`;
          const payload = {
            id: idempotency_key, idempotency_key, receipt_no, store_id: store?.id || user.store_id || register?.store_id, branch_id: register?.branch_id,
            register_id: register?.id, device_id: DEVICE_ID, customer_id: customer?.id,
            items: cart.map(({ stock, ...i }) => i), payments, order_discount: 0,
            created_offline: !navigator.onLine, local_created_at: new Date().toISOString(),
          };
          await enqueue({ id: idempotency_key, entity: "sale", idempotency_key, payload });
          await idb.put("sales", { ...payload, ...totals, change_given: change, cashier_name: user.name, created_at: payload.local_created_at });
          // Local decrement of cached stock for immediate UX
          setProducts((ps) => ps.map((p) => { const ci = cart.find((c) => c.product_id === p.id); return ci ? { ...p, quantity: p.quantity - ci.quantity } : p; }));
          const rcpt = { ...payload, ...totals, change_given: change, merchant, store, register, cashier: user.name, customer, tax };
          setReceipt(rcpt); setPayOpen(false); setCart([]); setCustomer(null);
          runSync(DEVICE_ID);
        }} />
      )}
      {ordersOpen && <OnlineOrdersModal orders={orders} onClose={() => setOrdersOpen(false)} onChanged={async (o) => { await idb.put("online_orders", o); setOrders((os) => os.map((x) => (x.id === o.id ? o : x))); if (o.status === "cancelled") setProducts((ps) => ps.map((p) => (p.id === o.product_id ? { ...p, quantity: p.quantity + o.quantity } : p))); }} />}
      {closeOpen && <CloseShiftModal shift={shift} onClose={() => setCloseOpen(false)} onClosed={() => { setCloseOpen(false); setShift(null); }} />}
      {returnOpen && <ReturnModal ctx={{ shift, register, store, merchant, user, tax }} initialCode={returnCode} onClose={() => { setReturnOpen(false); setReturnCode(""); }}
        onRefunded={(items) => { setProducts((ps) => ps.map((p) => { const ri = items.find((i) => i.product_id === p.id); return ri ? { ...p, quantity: p.quantity + ri.quantity } : p; })); flash("Refund recorded — stock restored"); }} />}
      {heldOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setHeldOpen(false)}>
          <div className="ms-card p-5 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-3"><h3 className="font-bold">Held Sales</h3><button onClick={() => setHeldOpen(false)}><X size={18} /></button></div>
            {held.length === 0 && <div className="text-slate-500 text-sm py-6 text-center">No held sales</div>}
            {held.map((h) => (
              <button key={h.id} data-testid={`held-${h.id}`} onClick={() => retrieveHeld(h)} className="w-full ms-card ms-card-hover rounded-lg p-3 mb-2 text-left flex justify-between">
                <span className="text-sm">{h.cart.length} items · {new Date(h.at).toLocaleTimeString()}</span>
                <span className="mono font-semibold">{fmtGYD(h.total)}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      {receipt && <ReceiptModal r={receipt} onClose={() => setReceipt(null)} />}
      {toast && <div data-testid="pos-toast" className="fixed bottom-5 left-1/2 -translate-x-1/2 bg-slate-800 border border-slate-600 px-4 py-2 rounded-lg text-sm z-50">{toast}</div>}
    </div>
  );
}

function ShiftGate({ user, register, store, onOpen, logout }) {
  const [cash, setCash] = useState("");
  const [err, setErr] = useState("");
  const open = async () => {
    try {
      const { data } = await api.post("/shifts/open", { register_id: register?.id, store_id: store?.id, branch_id: register?.branch_id, opening_cash: Number(cash || 0) });
      onOpen(data);
    } catch (e) { setErr(apiErr(e)); }
  };
  return (
    <div className="pos-dark min-h-screen flex items-center justify-center p-4">
      <div className="ms-card p-8 w-full max-w-sm text-center fade-up">
        <Clock className="mx-auto text-indigo-400 mb-3" size={36} />
        <h2 className="text-xl font-bold">Open Shift</h2>
        <p className="text-slate-400 text-sm mb-5">{user.name} · {register?.register_number || "Register"}</p>
        <label className="text-xs text-slate-400 text-left block">Opening cash (GYD)</label>
        <input data-testid="shift-opening-cash" type="number" value={cash} onChange={(e) => setCash(e.target.value)}
          className="w-full mt-1 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2.5 mono outline-none focus:border-indigo-500" placeholder="0.00" />
        {err && <div className="text-rose-400 text-sm mt-2">{err}</div>}
        <button data-testid="shift-open-btn" onClick={open} className="w-full touch-btn bg-emerald-600 hover:bg-emerald-500 rounded-lg py-2.5 font-semibold mt-4">Start Selling</button>
        <button onClick={logout} className="text-slate-500 text-sm mt-3">Sign out</button>
      </div>
    </div>
  );
}

function CustomerPicker({ customers, customer, setCustomer, onCreate }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(""); const [phone, setPhone] = useState("");
  return (
    <div className="relative">
      <button data-testid="pos-customer" onClick={() => setOpen((o) => !o)} className="w-full touch-btn ms-card ms-card-hover rounded-lg px-3 py-2 text-sm flex items-center gap-2">
        <User size={15} /> {customer ? customer.name : "Walk-in customer"}
      </button>
      {open && (
        <div className="absolute bottom-full mb-2 left-0 right-0 ms-card rounded-lg p-2 max-h-64 overflow-y-auto z-20">
          <button onClick={() => { setCustomer(null); setOpen(false); }} className="w-full text-left px-2 py-1.5 rounded hover:bg-slate-800 text-sm">Walk-in customer</button>
          {customers.map((c) => (
            <button key={c.id} onClick={() => { setCustomer(c); setOpen(false); }} className="w-full text-left px-2 py-1.5 rounded hover:bg-slate-800 text-sm">{c.name} <span className="text-slate-500 mono">{c.phone}</span></button>
          ))}
          <div className="border-t border-slate-700 mt-2 pt-2 space-y-1.5">
            <input data-testid="new-customer-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="New customer name" className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm" />
            <input data-testid="new-customer-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone" className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm mono" />
            <button data-testid="new-customer-save" disabled={!name} onClick={() => { onCreate({ name, phone }); setName(""); setPhone(""); setOpen(false); }} className="w-full touch-btn bg-indigo-600 rounded py-1.5 text-sm disabled:opacity-50">Add customer</button>
          </div>
        </div>
      )}
    </div>
  );
}


const METHODS = [
  { key: "cash", label: "Cash" }, { key: "card", label: "Card" },
  { key: "mobile_money", label: "Mobile Money (MMG)" }, { key: "bank_transfer", label: "Bank Transfer" },
];
const METHOD_LABEL = Object.fromEntries(METHODS.map((m) => [m.key, m.label]));
const QUICK_CASH = [500, 1000, 2000, 5000];

function PaymentModal({ total, onClose, onConfirm }) {
  const [method, setMethod] = useState("cash");
  const [tendered, setTendered] = useState("");
  const [ref, setRef] = useState("");
  const [parts, setParts] = useState([]);
  const [err, setErr] = useState("");
  const paid = +parts.reduce((s, p) => s + p.amount, 0).toFixed(2);
  const remaining = +(total - paid).toFixed(2);
  const amt = Number(tendered || 0);
  const isCash = method === "cash";
  const change = isCash && amt > remaining ? +(amt - remaining).toFixed(2) : 0;
  const partial = amt > 0 && amt < remaining - 0.001;
  const settled = remaining <= 0.001;

  const addPart = () => {
    if (amt <= 0 || settled) return;
    const amount = Math.min(amt, remaining);
    setParts((s) => [...s, { method, amount: +amount.toFixed(2), reference: ref || null, verification: isCash ? null : "externally verified" }]);
    setTendered(""); setRef(""); setErr("");
  };
  const removePart = (i) => setParts((s) => s.filter((_, idx) => idx !== i));

  const confirm = () => {
    if (settled) { onConfirm(parts, 0); return; }
    if (!isCash && partial) { setErr(`${METHOD_LABEL[method]} covers only part of the balance — add it as a split, then take the rest by another method.`); return; }
    const final = {
      method, amount: remaining, reference: ref || null,
      verification: isCash ? null : "externally verified",
      tendered: isCash && amt > remaining ? +amt.toFixed(2) : null,
    };
    onConfirm([...parts, final], change);
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="ms-card p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-3"><h3 className="font-bold text-lg">Payment</h3><button data-testid="pay-close" onClick={onClose}><X size={20} /></button></div>
        <div className="grid grid-cols-2 gap-3 mb-4 text-center">
          <div className="ms-card rounded-lg py-2"><div className="text-slate-400 text-xs">Sale total</div><div className="text-lg font-bold mono">{fmtGYD(total)}</div></div>
          <div className={`rounded-lg py-2 border ${settled ? "border-emerald-500/40 bg-emerald-500/10" : "border-indigo-500/40 bg-indigo-500/10"}`}><div className="text-slate-400 text-xs">{settled ? "Fully paid" : "Remaining due"}</div><div className={`text-lg font-bold mono ${settled ? "text-emerald-300" : "text-indigo-300"}`} data-testid="pay-remaining">{fmtGYD(remaining)}</div></div>
        </div>

        {parts.length > 0 && (
          <div className="mb-3 space-y-1" data-testid="pay-split-list">
            {parts.map((p, i) => (
              <div key={i} data-testid={`pay-split-${i}`} className="flex items-center justify-between text-sm ms-card rounded-lg px-3 py-1.5">
                <span className="text-slate-300">{METHOD_LABEL[p.method] || p.method}{p.reference && <span className="text-slate-500 text-xs mono ml-2">{p.reference}</span>}</span>
                <span className="flex items-center gap-2 mono">{fmtGYD(p.amount)}<button data-testid={`pay-split-remove-${i}`} onClick={() => removePart(i)} className="text-slate-500 hover:text-rose-400"><X size={14} /></button></span>
              </div>
            ))}
          </div>
        )}

        {!settled && (<>
          <div className="grid grid-cols-2 gap-2 mb-3">
            {METHODS.map((m) => (
              <button key={m.key} data-testid={`pay-method-${m.key}`} onClick={() => { setMethod(m.key); setErr(""); }} className={`touch-btn py-2.5 rounded-lg text-sm ${method === m.key ? "bg-indigo-600" : "ms-card ms-card-hover"}`}>{m.label}</button>
            ))}
          </div>
          <input data-testid="pay-amount" type="number" min="0" value={tendered} onChange={(e) => { setTendered(e.target.value); setErr(""); }}
            placeholder={isCash ? `Cash tendered (blank = exact ${fmtGYD(remaining)})` : `Amount (blank = ${fmtGYD(remaining)})`}
            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2.5 mono mb-2 outline-none focus:border-indigo-500" />
          {isCash && (
            <div className="grid grid-cols-5 gap-1.5 mb-2">
              <button data-testid="pay-quick-exact" onClick={() => setTendered(String(remaining))} className="touch-btn text-xs py-1.5 rounded-md ms-card ms-card-hover">Exact</button>
              {QUICK_CASH.map((n) => <button key={n} data-testid={`pay-quick-${n}`} onClick={() => setTendered(String(Math.max(n, Math.ceil(remaining / n) * n)))} className="touch-btn text-xs py-1.5 rounded-md ms-card ms-card-hover mono">{n.toLocaleString()}</button>)}
            </div>
          )}
          {!isCash && <input data-testid="pay-ref" value={ref} onChange={(e) => setRef(e.target.value)} placeholder="Reference / approval code" className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm mb-2" />}
          {!isCash && <div className="text-xs text-amber-300 mb-2">Records an externally-completed payment. Not processed by the POS.</div>}
          <button data-testid="pay-add-split" onClick={addPart} disabled={amt <= 0 || amt >= remaining - 0.001} className="w-full touch-btn text-xs py-2 rounded-lg ms-card ms-card-hover mb-2 disabled:opacity-40 flex items-center justify-center gap-1">
            <Plus size={13} /> {partial ? `Add ${METHOD_LABEL[method]} ${fmtGYD(amt)} and pay the rest another way` : amt >= remaining - 0.001 && amt > 0 ? "Covers the balance — use Complete below" : "Add as split payment (enter a partial amount)"}
          </button>
          {isCash && change > 0 && <div className="flex justify-between text-xl font-bold mt-2 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2"><span>Change due</span><span className="mono text-emerald-300" data-testid="pay-change">{fmtGYD(change)}</span></div>}
        </>)}
        {err && <div data-testid="pay-error" className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2 mb-1">{err}</div>}
        <button data-testid="pay-confirm" onClick={confirm} className="w-full touch-btn bg-emerald-600 hover:bg-emerald-500 rounded-lg py-3 font-bold mt-3 flex items-center justify-center gap-2">
          <Banknote size={18} /> {settled ? "Complete Sale" : `Complete — take ${fmtGYD(remaining)} by ${METHOD_LABEL[method]}`}
        </button>
      </div>
    </div>
  );
}

function CloseShiftModal({ shift, onClose, onClosed }) {
  const [cash, setCash] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const close = async () => {
    setBusy(true); setErr("");
    try {
      await api.post("/shifts/close", { shift_id: shift.id, actual_cash: Number(cash || 0) });
      const { data } = await api.get(`/shifts/${shift.id}/report`);
      setReport(data);
    } catch (e) { setErr(navigator.onLine ? apiErr(e) : "You must be online to close a shift"); } finally { setBusy(false); }
  };
  if (report) return <ZReportModal report={report} onClose={onClosed} closeLabel="Finish" />;
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="ms-card p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-3"><h3 className="font-bold text-lg flex items-center gap-2"><Lock size={18} className="text-amber-300" /> Close Shift</h3><button data-testid="close-shift-cancel" onClick={onClose}><X size={20} /></button></div>
        <p className="text-slate-400 text-sm mb-4">Count the cash in the drawer (including the opening float) and enter the total. The Z-report will show expected vs actual and any variance.</p>
        <label className="text-xs text-slate-400 block">Actual cash counted (GYD)</label>
        <input data-testid="close-shift-cash" type="number" min="0" value={cash} onChange={(e) => setCash(e.target.value)} autoFocus
          className="w-full mt-1 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2.5 mono text-lg outline-none focus:border-indigo-500" placeholder="0.00" />
        {err && <div data-testid="close-shift-error" className="text-rose-400 text-sm mt-2">{err}</div>}
        <button data-testid="close-shift-confirm" disabled={busy || cash === ""} onClick={close} className="w-full touch-btn bg-amber-600 hover:bg-amber-500 rounded-lg py-2.5 font-semibold mt-4 disabled:opacity-50">{busy ? "Closing…" : "Close Shift & Print Z-Report"}</button>
      </div>
    </div>
  );
}

function ReceiptModal({ r, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 print:bg-white print:block" onClick={onClose}>
      <div className="bg-white text-black rounded-lg w-full max-w-[320px] p-5 print:max-w-full mono text-xs" onClick={(e) => e.stopPropagation()} id="receipt">
        <div className="text-center mb-2">
          <div className="font-bold text-sm">{r.merchant?.name}</div>
          <div>{r.store?.address}</div>
          <div>{r.merchant?.phone}</div>
          {r.merchant?.business_number && <div>Biz#: {r.merchant.business_number}</div>}
        </div>
        <div className="border-t border-dashed border-black my-1" />
        <div>Receipt: {r.receipt_no}</div>
        <div>{new Date().toLocaleString()}</div>
        <div>Cashier: {r.cashier} · {r.register?.register_number}</div>
        {r.customer && <div>Customer: {r.customer.name}</div>}
        {r.created_offline && <div className="font-bold">** OFFLINE SALE — PENDING SYNC **</div>}
        <div className="border-t border-dashed border-black my-1" />
        {r.items.map((it, i) => (
          <div key={i} className="flex justify-between"><span>{it.quantity}× {it.name}</span><span>{(it.unit_price * it.quantity).toFixed(2)}</span></div>
        ))}
        <div className="border-t border-dashed border-black my-1" />
        <div className="flex justify-between"><span>Subtotal</span><span>{r.subtotal.toFixed(2)}</span></div>
        <div className="flex justify-between"><span>{r.tax?.tax_name || "Tax"}</span><span>{r.taxTotal.toFixed(2)}</span></div>
        <div className="flex justify-between font-bold text-sm"><span>TOTAL (GYD)</span><span>{r.total.toFixed(2)}</span></div>
        <div className="border-t border-dashed border-black my-1" />
        {r.payments.length > 1 && <div className="font-bold">SPLIT PAYMENT ({r.payments.length})</div>}
        {r.payments.map((p, i) => (
          <div key={i} data-testid={`receipt-payment-${i}`}>
            <div className="flex justify-between"><span>{METHOD_LABEL[p.method] || p.method}{p.reference ? ` #${p.reference}` : ""}</span><span>{p.amount.toFixed(2)}</span></div>
            {p.tendered && p.tendered > p.amount && <div className="flex justify-between pl-3 text-[10px]"><span>tendered</span><span>{p.tendered.toFixed(2)}</span></div>}
          </div>
        ))}
        {r.change_given > 0 && <div className="flex justify-between font-bold" data-testid="receipt-change"><span>CHANGE</span><span>{r.change_given.toFixed(2)}</span></div>}
        <div className="mt-3 text-center">
          <Code128 value={r.receipt_no} height={42} />
          <div className="text-[10px] tracking-widest mt-0.5">{r.receipt_no}</div>
          <div className="text-[9px] text-gray-600">Scan at register for returns</div>
        </div>
        <div className="text-center mt-2">Thank you! Returns within 7 days with receipt.</div>
        <div className="text-center text-[9px] mt-1">Ref: {r.idempotency_key.slice(0, 8)}</div>
      </div>
      <div className="absolute top-4 right-4 flex gap-2 print:hidden">
        <button data-testid="receipt-print" onClick={printReceipt} className="bg-indigo-600 rounded-lg px-4 py-2 text-sm flex items-center gap-2 text-white"><Printer size={16} /> Print</button>
        <button data-testid="receipt-close" onClick={onClose} className="bg-slate-700 rounded-lg px-4 py-2 text-sm text-white">New Sale</button>
      </div>
    </div>
  );
}
